/**
 * De cuánto es la recarga, sin abrir ninguna ventana.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  POR QUÉ ESTO ES UNA FRANJA Y NO UN CUADRO
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Es la misma decisión que en `BarraDePeso`, y por el mismo motivo del mundo
 * real. Un bodegón que hace recargas las hace en tanda: entra gente y son
 * «quinientos a este número», «mil a este otro», «trescientos de Robux». Con un
 * cuadro que se abre encima, eso son tres ventanas abriéndose y cerrándose y —
 * en tablet— tres veces el teclado subiendo y bajando.
 *
 * Esta franja aparece debajo del buscador, no tapa la cuadrícula, y **no se
 * cierra al agregar**: se queda puesta y lista para el monto siguiente.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  ⚠️ SE ESCRIBE EL MONTO, SE ENSEÑA LO QUE SE COBRA
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * El cliente dice «recárgame 500». El cajero escribe 500 —lo que oyó, sin
 * cuentas de cabeza— y la franja le contesta en grande **«cobras 750»**, que es
 * lo que tiene que pedirle.
 *
 * Al revés no sirve: si la casilla pidiera el cobro, el cajero tendría que
 * calcular 500 + 50% con el cliente delante, que es justo el error que este
 * servicio existe para quitar.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Check, X } from "lucide-react";

import { Boton, cx } from "@/components/ui";
import { formatMoney, type FormatoMoneda } from "@/lib/money";
import { useLimpiarMonto } from "@/lib/pasoDeMoneda";
import {
  atajosDe,
  comoSeCobra,
  loQueSeCobra,
  loQueSeGana,
  recargoDe,
  type ItemDeRecarga,
} from "./recargas";

type Props = {
  item: ItemDeRecarga;
  formato: FormatoMoneda;
  onAgregar: (monto: number) => void;
  onCerrar: () => void;
};

export function BarraDeRecarga({ item, formato, onAgregar, onCerrar }: Props) {
  const recargo = useMemo(() => recargoDe(item), [item]);

  const limpiar = useLimpiarMonto();
  const [texto, setTexto] = useState("");
  const casilla = useRef<HTMLInputElement>(null);

  /*
    Los atajos se leen del dispositivo al cambiar de servicio, y se vuelven a
    leer cada vez que se agrega uno —de ahí el contador—. Sin eso, el monto que
    el cajero acaba de usar por tercera vez no aparecería de botón hasta
    recargar la página.
  */
  const [vueltas, setVueltas] = useState(0);
  const atajos = useMemo(() => atajosDe(item.id), [item.id, vueltas]);

  /*
    Al cambiar de servicio se limpia y se enfoca.

    Es lo que permite despachar de corrido: se toca «Recarga Movistar» y la
    franja ya está lista, sin arrastrar los 500 de la anterior — que sería el
    fallo callado de este diseño: recargar 500 a quien pidió 1.000.
  */
  useEffect(() => {
    setTexto("");
    casilla.current?.focus();
    casilla.current?.select();
  }, [item.id]);

  const monto = Number.parseFloat(texto.replace(",", ".")) || 0;
  const cobro = loQueSeCobra(monto, recargo);
  const ganancia = loQueSeGana(monto, recargo);
  const hayRecargo = recargo.pct > 0 || recargo.fijo > 0;

  function confirmar(e: React.FormEvent) {
    e.preventDefault();
    if (monto <= 0) return;
    onAgregar(monto);
    // No se cierra: se limpia y se queda lista. Ver el encabezado.
    setTexto("");
    setVueltas((v) => v + 1);
    casilla.current?.focus();
  }

  return (
    <form
      onSubmit={confirmar}
      className="animar-entrada rounded-[var(--radius-card)] border border-accent bg-accent-soft p-3"
    >
      <div className="flex flex-wrap items-end gap-3">
        {/* Qué se está recargando, y con qué regla */}
        <div className="min-w-0 flex-1 basis-full sm:basis-auto">
          <p className="truncate text-sm font-semibold text-ink">{item.name}</p>
          <p className="text-xs text-muted">
            {hayRecargo
              ? `Cobras ${comoSeCobra(recargo, formato.symbol)} sobre el monto`
              : "Se cobra lo mismo que se recarga"}
          </p>
        </div>

        {/*
          Los montos que este negocio pide de verdad.

          Empiezan vacíos —no hay lista escrita a mano— y salen solos de lo que
          se vaya vendiendo. Ver `atajosDe` en `recargas.ts`.
        */}
        {atajos.length > 0 && (
          <div className="flex shrink-0 flex-wrap gap-1">
            {atajos.map((atajo) => (
              <button
                key={atajo}
                type="button"
                onClick={() => setTexto(String(atajo))}
                className={cx(
                  "pulsable tabular h-10 min-w-[3.75rem] rounded-[var(--radius-control)] border px-2 text-sm font-semibold transition-colors",
                  monto === atajo
                    ? "border-accent bg-accent text-on-accent"
                    : "border-line-strong bg-surface text-ink-soft hover:border-accent",
                )}
              >
                {atajo}
              </button>
            ))}
          </div>
        )}

        {/* De cuánto */}
        <label className="flex h-10 shrink-0 items-center gap-1.5 rounded-[var(--radius-control)] border border-line-strong bg-surface px-2.5 focus-within:border-accent">
          <span className="text-xs text-muted">{formato.symbol}</span>
          <input
            ref={casilla}
            type="text"
            inputMode="decimal"
            value={texto}
            onChange={(e) => setTexto(limpiar(e.target.value, formato.code))}
            placeholder="0"
            aria-label="De cuánto es la recarga"
            className="tabular w-20 bg-transparent text-base outline-none"
          />
        </label>

        {/*
          ⚠️ LO QUE SE COBRA, EN GRANDE.

          Es el número que el cajero le va a decir al cliente. Puesto en letra
          pequeña al lado del monto se confunde con él, y cobrar 500 donde
          decía 750 no da ningún error: la venta cuadra y el negocio pierde el
          recargo entero.
        */}
        {/*
          ⚠️ «GANAS» VA DENTRO DE ESTE BLOQUE, NO AL LADO.

          Estaba suelto en la fila, y en pantalla estrecha se envolvía: acababa
          debajo, separado del número al que se refiere y pegado al botón de
          agregar. Se leía «ganas $100,00 · Agregar», que no significa nada.

          Aquí abajo del cobro no se puede despegar de él, y se lee como lo que
          es: cobras 300, de eso ganas 100.
        */}
        <div className="shrink-0 leading-tight">
          <p className="text-[0.65rem] uppercase tracking-wide text-muted">Cobras</p>
          <p
            className={cx(
              "tabular text-xl font-bold",
              cobro > 0 ? "text-accent" : "text-muted",
            )}
          >
            {formatMoney(cobro, formato)}
          </p>
          {/* Lo que queda para el negocio. Solo cuando hay algo que decir. */}
          {hayRecargo && ganancia > 0 && (
            <p className="tabular text-xs text-muted">
              ganas {formatMoney(ganancia, formato)}
            </p>
          )}
        </div>

        <Boton type="submit" disabled={monto <= 0} className="h-10 shrink-0">
          <Check size={16} />
          Agregar
        </Boton>

        <button
          type="button"
          onClick={onCerrar}
          aria-label="Cerrar"
          className="h-10 shrink-0 rounded-[var(--radius-control)] px-2 text-muted transition-colors hover:bg-surface-hover hover:text-ink"
        >
          <X size={17} />
        </button>
      </div>
    </form>
  );
}
